import { zodResolver } from '@hookform/resolvers/zod';
import type { InvitationLookup } from '@noconflict/contracts';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import { useSession } from '@/entities/session';
import { useDocumentTitle } from '@/shared/lib/use-document-title';
import { useAcceptInvitation, useInvitationLookup } from '../../entities/invitation';
import { confirmationRedirect, rememberPendingEmail } from '../../features/auth/pending-email';
import { identityErrorMessage } from '../../shared/api/identity-error';
import { supabase } from '../../shared/api/supabase';
import { validationMessage } from '../../shared/i18n/zod';
import { AuthLayout } from '../../shared/ui/auth-layout';
import { Button } from '../../shared/ui/button';
import { Field } from '../../shared/ui/field';
import { Input } from '../../shared/ui/input';
import { Skeleton } from '../../shared/ui/skeleton';

const name = (required: 'firstNameRequired' | 'lastNameRequired') =>
  z.string().trim().min(1, validationMessage(required)).max(80, validationMessage('nameMax'));
const schema = z.object({
  firstName: name('firstNameRequired'),
  lastName: name('lastNameRequired'),
  password: z.string().min(8, validationMessage('passwordMin')),
});
type Values = z.infer<typeof schema>;

/**
 * `/invite/:token`, the link in an invitation email. Works signed out: whoever holds the
 * token may see the org's name and the invited address. Joining always needs a session whose confirmed email
 * is the invited one; the database checks that, this page only explains.
 */
export function InvitePage() {
  const { t } = useTranslation('invite');
  const { token = '' } = useParams();
  const { session, loading } = useSession();
  const lookup = useInvitationLookup(token);
  const invitation = lookup.data ?? null;
  useDocumentTitle(
    invitation ? t('title', { org: invitation.organization_name }) : t('invalidTitle'),
  );

  if (loading || lookup.isPending)
    return (
      <AuthLayout title={<Skeleton className="h-9 w-56" />}>
        <section aria-busy="true" aria-label={t('loading')} className="grid gap-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </section>
      </AuthLayout>
    );
  if (lookup.isError)
    return (
      <AuthLayout title={t('invalidTitle')}>
        <p role="alert">{identityErrorMessage(lookup.error)}</p>
      </AuthLayout>
    );
  if (invitation?.status !== 'pending') {
    const codes = {
      expired: 'invitation_expired',
      revoked: 'invitation_revoked',
      accepted: 'invitation_used',
    } as const;
    const code = invitation ? codes[invitation.status] : 'invitation_not_found';
    return (
      <AuthLayout
        title={invitation ? t('title', { org: invitation.organization_name }) : t('invalidTitle')}
        footer={
          <p>
            <Link to={session ? '/' : '/sign-in'}>{session ? t('goHome') : t('signIn')}</Link>
          </p>
        }
      >
        <p>{identityErrorMessage(new Error(code))}</p>
      </AuthLayout>
    );
  }
  const title = t('title', { org: invitation.organization_name });
  if (!session) return <SignedOut token={token} invitation={invitation} title={title} />;
  const mine = (session.user.email ?? '').toLowerCase() === invitation.email;
  return mine ? (
    <Accept token={token} invitation={invitation} title={title} />
  ) : (
    <WrongAccount invitation={invitation} title={title} current={session.user.email ?? ''} />
  );
}

function Address({ children }: { children: string }) {
  return <strong className="wrap-anywhere">{children}</strong>;
}

/** New person: names and a password. The address is the invited one; no country, because no org is created. */
function SignedOut({
  token,
  invitation,
  title,
}: {
  token: string;
  invitation: InvitationLookup;
  title: string;
}) {
  const { t } = useTranslation(['invite', 'auth']);
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: '', lastName: '', password: '' },
  });
  const next = `/invite/${token}`;
  return (
    <AuthLayout
      title={title}
      footer={
        <p>
          {t('auth:signUp.haveAccount')}{' '}
          <Link to={`/sign-in?next=${encodeURIComponent(next)}`}>{t('auth:signUp.signIn')}</Link>
        </p>
      }
    >
      <form
        className="space-y-4"
        noValidate
        onSubmit={handleSubmit(async (values) => {
          setServerError(null);
          const { data, error } = await supabase.auth.signUp({
            email: invitation.email,
            password: values.password,
            options: {
              // the signup trigger sees a live invitation for this address and creates no personal org (Q2)
              data: {
                first_name: values.firstName,
                last_name: values.lastName,
                invitation_token: token,
              },
              emailRedirectTo: confirmationRedirect(next),
            },
          });
          if (error && error.code !== 'user_already_exists') {
            setServerError(error.message);
            return;
          }
          // a session means confirmation is off: this page re-renders signed in and offers to accept
          if (data.session) return;
          rememberPendingEmail(invitation.email);
          navigate('/check-email');
        })}
      >
        <p>{t('createAccountLead', { org: invitation.organization_name })}</p>
        <Field label={t('auth:fields.email')} description={t('emailLocked')}>
          <Input
            id="invite-email"
            type="email"
            readOnly
            value={invitation.email}
            autoComplete="email"
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('auth:fields.firstName')} error={formState.errors.firstName?.message}>
            <Input id="first-name" autoComplete="given-name" {...register('firstName')} />
          </Field>
          <Field label={t('auth:fields.lastName')} error={formState.errors.lastName?.message}>
            <Input id="last-name" autoComplete="family-name" {...register('lastName')} />
          </Field>
        </div>
        <Field label={t('auth:fields.password')} error={formState.errors.password?.message}>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            {...register('password')}
          />
        </Field>
        {serverError ? (
          <p role="alert" className="text-sm text-destructive">
            {serverError}
          </p>
        ) : null}
        <Button type="submit" className="w-full" disabled={formState.isSubmitting}>
          {t('auth:signUp.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}

function Accept({
  token,
  invitation,
  title,
}: {
  token: string;
  invitation: InvitationLookup;
  title: string;
}) {
  const { t } = useTranslation('invite');
  const navigate = useNavigate();
  const accept = useAcceptInvitation();
  const [error, setError] = useState<string | null>(null);
  return (
    <AuthLayout title={title}>
      <div className="space-y-4">
        <p>
          <Trans
            t={t}
            i18nKey="acceptLead"
            values={{ org: invitation.organization_name, email: invitation.email }}
            components={{ address: <Address>{invitation.email}</Address> }}
          />
        </p>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <Button
          className="w-full"
          disabled={accept.isPending}
          onClick={async () => {
            setError(null);
            try {
              const joined = await accept.mutateAsync(token);
              navigate(`/o/${joined.organization_slug}`);
            } catch (e) {
              setError(identityErrorMessage(e));
            }
          }}
        >
          {t('accept')}
        </Button>
      </div>
    </AuthLayout>
  );
}

function WrongAccount({
  invitation,
  title,
  current,
}: {
  invitation: InvitationLookup;
  title: string;
  current: string;
}) {
  const { t } = useTranslation(['invite', 'auth']);
  return (
    <AuthLayout title={title}>
      <div className="space-y-4">
        <p>
          <Trans
            t={t}
            i18nKey="sentTo"
            values={{ email: invitation.email }}
            components={{ address: <Address>{invitation.email}</Address> }}
          />
        </p>
        <p>
          <Trans
            t={t}
            i18nKey="signedInAs"
            values={{ email: current }}
            components={{ address: <Address>{current}</Address> }}
          />
        </p>
        <Button variant="secondary" className="w-full" onClick={() => void supabase.auth.signOut()}>
          {t('auth:signOut')}
        </Button>
      </div>
    </AuthLayout>
  );
}
